import React from 'react';
import { Play, Clock, List } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import ContractBadge from './ContractBadge';
import LanguageBadge from './LanguageBadge';
import ShareButton from './ShareButton';

const PlaylistItemCard = ({ 
  item, 
  index,
  videoNumber,
  isCurrentVideo = false,
  onItemClick,
  shareUrl,
  showShareButton = true,
  variant = 'small', // 'small' for VideoPage, 'large' for PlaylistPage
  className = ''
}) => {
  const { t } = useLanguage();
  const { hasAccess } = useAuth();
  
  if (item.type === "divider") {
    return (
      <div className="flex items-center py-3">
        <div className="flex-1 border-t border-gray-300"></div>
        <div className="px-3 text-sm text-gray-500">
          {t({ en: "Section Break", de: "Abschnittstrenner" })}
        </div>
        <div className="flex-1 border-t border-gray-300"></div>
      </div>
    );
  }

  const canAccess = hasAccess(item.contract, item.roles_allowed);
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleClick = () => {
    if (canAccess && !isCurrentVideo && onItemClick) {
      onItemClick(item, index);
    }
  };
  
  // Styling based on variant
  const thumbnailSize = variant === 'large' 
    ? 'w-full sm:w-24 h-32 sm:h-[3.375rem]' 
    : 'w-16 sm:w-20 h-[2.25rem] sm:h-[2.8125rem]';
    
  const padding = variant === 'large' ? 'p-0 sm:p-4' : 'p-2 sm:p-3';
  const titleSize = variant === 'large' ? 'text-sm sm:text-base' : 'text-xs sm:text-sm';
  const descriptionSize = variant === 'large' ? 'text-xs sm:text-sm' : 'text-xs';
  const descriptionLines = variant === 'large' ? 'line-clamp-2' : 'line-clamp-1';
  const spacing = variant === 'large' ? 'ml-3 sm:ml-4' : 'ml-2 sm:ml-3';

  return (
    <div className={`${className}`}>
      <div className="flex items-stretch">
        <button
          onClick={handleClick}
          disabled={!canAccess || isCurrentVideo}
          className={`flex-1 ${padding} rounded-lg transition-colors text-left ${
            isCurrentVideo 
              ? "bg-[#00A3E0] bg-opacity-10 border-2 border-[#00A3E0]" 
              : "border border-gray-200"
          } ${
            canAccess && !isCurrentVideo
              ? "hover:bg-gray-50 cursor-pointer hover:shadow-md"
              : isCurrentVideo ? "opacity-60 cursor-not-allowed" : "opacity-60 cursor-not-allowed pointer-events-none"
          } overflow-hidden`}
          type="button"
        >
          {/* Mobile Layout - Card Style */}
          <div className="sm:hidden">
            {variant === 'large' ? (
              // Large variant - Card layout for mobile
              <div className="relative">
                {/* Thumbnail as background */}
                <div className="w-full h-32 bg-gray-100 relative">
                  <img
                    src={item.thumbnailUrl}
                    alt={t(item.title)}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  
                  {/* Duration badge */}
                  {item.duration && item.duration > 0 && (
                    <div className="absolute top-2 right-2 bg-black bg-opacity-75 text-white text-xs px-1.5 py-0.5 rounded flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatDuration(item.duration)}
                    </div>
                  )}

                  {/* Title on image */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                    <h3 className="text-sm font-medium line-clamp-2">
                      {videoNumber > 0 ? `${videoNumber}. ` : ''}{t(item.title)}
                    </h3>
                  </div>
                </div>
                
                {/* Bottom section with badges */}
                <div className="p-3 pt-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      {item.type === "video" && item.language && (
                        <LanguageBadge language={item.language} />
                      )}
                      <ContractBadge contract={item.contract} />
                    </div>
                    {item.type === "video" ? (
                      <Play className="w-4 h-4 text-[#00A3E0]" />
                    ) : (
                      <List className="w-4 h-4 text-[#00A3E0]" />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Small variant - Compact layout
              <div className="flex items-center">
                <div className="flex-shrink-0 w-16 h-[2.25rem] bg-gray-100 rounded overflow-hidden relative">
                  <img
                    src={item.thumbnailUrl}
                    alt={t(item.title)}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  {item.duration && item.duration > 0 && (
                    <div className="absolute bottom-0 right-0 bg-black bg-opacity-90 text-white text-[9px] px-0.5 rounded-tl flex items-center">
                      {formatDuration(item.duration)}
                    </div>
                  )}
                </div>
                
                <div className="ml-2 flex-1 min-w-0">
                  <h3 className="text-xs font-medium text-[#002855] line-clamp-1">
                    {videoNumber > 0 ? `${videoNumber}. ` : ''}{t(item.title)}
                  </h3>
                  <div className="flex items-center gap-1 mt-1">
                    {item.type === "video" && item.language && (
                      <LanguageBadge language={item.language} />
                    )}
                    <ContractBadge contract={item.contract} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Layout - Keep existing */}
          <div className="hidden sm:flex sm:items-center">
            <div className={`flex-shrink-0 ${thumbnailSize} bg-gray-100 ${variant === 'large' ? 'rounded-lg' : 'rounded'} overflow-hidden relative`}>
              <img
                src={item.thumbnailUrl}
                alt={t(item.title)}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              {item.duration && item.duration > 0 && (
                <div className={`absolute bottom-1 ${variant === 'large' ? 'left-1/2 transform -translate-x-1/2' : 'right-1'} bg-black bg-opacity-75 text-white text-[10px] px-1 py-0.5 rounded flex items-center`}>
                  <Clock className="w-2.5 h-2.5 mr-0.5" />
                  {formatDuration(item.duration)}
                </div>
              )}
            </div>

            <div className={`${spacing} flex-1 min-w-0`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-2">
                  <h3 className={`${titleSize} font-medium text-[#002855] truncate`}>
                    {videoNumber > 0 ? `${videoNumber}. ` : ''}{t(item.title)}
                  </h3>
                  {item.description && (
                    <p className={`${descriptionSize} text-gray-500 mt-1 ${descriptionLines}`}>
                      {t(item.description)}
                    </p>
                  )}
                </div>
                
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {item.type === "video" && item.language && (
                    <LanguageBadge language={item.language} />
                  )}
                  <ContractBadge contract={item.contract} />
                  {item.type === "video" ? (
                    <Play className="w-4 h-4 text-[#00A3E0]" />
                  ) : (
                    <List className="w-4 h-4 text-[#00A3E0]" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </button>
        
        {/* Share button */}
        {showShareButton && item.type === "video" && shareUrl && (
          <div className="ml-1 sm:ml-2 flex-shrink-0">
            <ShareButton 
              url={shareUrl}
              title={t(item.title)}
              description={t(item.description) || 'Watch this video on burnair Academy'}
              variant="icon"
              size="small"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaylistItemCard;